import { z } from 'zod';
import { RE2JS } from 're2js';

// src/enums.ts
var SEVERITIES = ["critical", "major", "warning", "info"];
var SeveritySchema = z.enum(SEVERITIES);
var ALARM_STATES = ["open", "acknowledged", "snoozed", "closed"];
var AlarmStateSchema = z.enum(ALARM_STATES);
var ACTIVE_ALARM_STATES = ["open", "acknowledged", "snoozed"];
var RULE_SETTABLE_STATES = ["acknowledged", "closed"];
var EVENT_TYPES = ["trigger", "update", "resolve"];
var EventTypeSchema = z.enum(EVENT_TYPES);
var ENTITY_KINDS = [
  "host",
  "service",
  "device",
  "room",
  "tenant",
  "region",
  "service_offering",
  "url",
  "other"
];
var EntityKindSchema = z.enum(ENTITY_KINDS);
var KNOWN_SOURCE_TYPES = [
  "webhook",
  "nagios",
  "icinga2",
  "zabbix",
  "prtg",
  "solarwinds",
  "prometheus",
  "alertmanager",
  "grafana",
  "datadog",
  "azure_monitor",
  "azure_service_health",
  "m365_service_health",
  "entra_health",
  "entra_risk_detections",
  "teams_rooms_pro",
  "logitech_sync",
  "statuspage",
  "rss",
  "slack_status",
  "gcp_status",
  "azure_public_status",
  "email",
  "email_mailbox",
  "mapped"
];
var SourceTypeSchema = z.string().min(1).max(64);
function isKnownSourceType(value) {
  return KNOWN_SOURCE_TYPES.includes(value);
}
var EMAIL_TEMPLATE_NAMES = [
  "nagios-email",
  "teams-rooms-pro-email",
  "m365-service-health",
  "entra-connect-health",
  "logitech-sync"
];
var EmailTemplateNameSchema = z.enum(EMAIL_TEMPLATE_NAMES);
var SEVERITY_RANK = {
  critical: 4,
  major: 3,
  warning: 2,
  info: 1
};
function compareSeverity(a, b) {
  return SEVERITY_RANK[b] - SEVERITY_RANK[a];
}
function isMoreSevere(a, b) {
  return SEVERITY_RANK[a] > SEVERITY_RANK[b];
}
function maxSeverity(...args) {
  const list = Array.isArray(args[0]) ? args[0] : args;
  let best;
  for (const s of list) {
    if (best === void 0 || SEVERITY_RANK[s] > SEVERITY_RANK[best]) best = s;
  }
  return best;
}
function minSeverity(severities) {
  let best;
  for (const s of severities) {
    if (best === void 0 || SEVERITY_RANK[s] < SEVERITY_RANK[best]) best = s;
  }
  return best;
}

// src/sha256.ts
var K = new Uint32Array([
  1116352408,
  1899447441,
  3049323471,
  3921009573,
  961987163,
  1508970993,
  2453635748,
  2870763221,
  3624381080,
  310598401,
  607225278,
  1426881987,
  1925078388,
  2162078206,
  2614888103,
  3248222580,
  3835390401,
  4022224774,
  264347078,
  604807628,
  770255983,
  1249150122,
  1555081692,
  1996064986,
  2554220882,
  2821834349,
  2952996808,
  3210313671,
  3336571891,
  3584528711,
  113926993,
  338241895,
  666307205,
  773529912,
  1294757372,
  1396182291,
  1695183700,
  1986661051,
  2177026350,
  2456956037,
  2730485921,
  2820302411,
  3259730800,
  3345764771,
  3516065817,
  3600352804,
  4094571909,
  275423344,
  430227734,
  506948616,
  659060556,
  883997877,
  958139571,
  1322822218,
  1537002063,
  1747873779,
  1955562222,
  2024104815,
  2227730452,
  2361852424,
  2428436474,
  2756734187,
  3204031479,
  3329325298
]);
function rotr(x, n) {
  return x >>> n | x << 32 - n;
}
function utf8Encode(input) {
  return new TextEncoder().encode(input);
}
function sha256Hex(input) {
  const bytes = typeof input === "string" ? utf8Encode(input) : input;
  const bitLength = bytes.length * 8;
  const paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(bytes);
  padded[bytes.length] = 128;
  const view = new DataView(padded.buffer);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 4294967296), false);
  view.setUint32(paddedLength - 4, bitLength >>> 0, false);
  const h = new Uint32Array([
    1779033703,
    3144134277,
    1013904242,
    2773480762,
    1359893119,
    2600822924,
    528734635,
    1541459225
  ]);
  const w = new Uint32Array(64);
  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4, false);
    for (let i = 16; i < 64; i++) {
      const w15 = w[i - 15];
      const w2 = w[i - 2];
      const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ w15 >>> 3;
      const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ w2 >>> 10;
      w[i] = w[i - 16] + s0 + w[i - 7] + s1 >>> 0;
    }
    let a = h[0];
    let b = h[1];
    let c = h[2];
    let d = h[3];
    let e = h[4];
    let f = h[5];
    let g = h[6];
    let hh = h[7];
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = e & f ^ ~e & g;
      const t1 = hh + S1 + ch + K[i] + w[i] >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = a & b ^ a & c ^ b & c;
      const t2 = S0 + maj >>> 0;
      hh = g;
      g = f;
      f = e;
      e = d + t1 >>> 0;
      d = c;
      c = b;
      b = a;
      a = t1 + t2 >>> 0;
    }
    h[0] = h[0] + a >>> 0;
    h[1] = h[1] + b >>> 0;
    h[2] = h[2] + c >>> 0;
    h[3] = h[3] + d >>> 0;
    h[4] = h[4] + e >>> 0;
    h[5] = h[5] + f >>> 0;
    h[6] = h[6] + g >>> 0;
    h[7] = h[7] + hh >>> 0;
  }
  let out = "";
  for (let i = 0; i < 8; i++) out += h[i].toString(16).padStart(8, "0");
  return out;
}
function stableStringify(value) {
  if (value === void 0) return "null";
  if (typeof value === "bigint") return JSON.stringify(value.toString());
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map((v) => stableStringify(v)).join(",")}]`;
  if (value instanceof Date) return JSON.stringify(value.toISOString());
  const entries = Object.entries(value).filter(([, v]) => v !== void 0).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(",")}}`;
}

// src/event.ts
var TITLE_MAX_LENGTH = 1e3;
var DEDUP_KEY_MAX_LENGTH = 1024;
var rfc3339 = z.iso.datetime({ offset: true });
var EntitySchema = z.object({
  name: z.string().min(1).max(512),
  kind: EntityKindSchema,
  external_id: z.string().max(512).optional(),
  url: z.string().max(2048).optional()
});
var CanonicalEventSchema = z.object({
  integration_id: z.string().min(1),
  source_type: SourceTypeSchema,
  source_event_id: z.string().min(1).max(512),
  dedup_key: z.string().min(1).max(DEDUP_KEY_MAX_LENGTH),
  sequence: z.number().int().nonnegative().optional(),
  event_type: EventTypeSchema,
  severity: SeveritySchema,
  title: z.string().min(1).max(TITLE_MAX_LENGTH),
  message: z.string().default(""),
  entity: EntitySchema,
  category: z.string().max(256).optional(),
  occurred_at: rfc3339,
  received_at: rfc3339,
  fields: z.record(z.string(), z.unknown()).default({}),
  tags: z.array(z.string().min(1).max(128)).default([]),
  raw: z.unknown()
});
var DERIVED_SOURCE_EVENT_ID_PREFIX = "sha256:";
function deriveSourceEventId(integrationId, rawBody) {
  const body = typeof rawBody === "string" ? rawBody : stableStringify(rawBody);
  return `${DERIVED_SOURCE_EVENT_ID_PREFIX}${sha256Hex(`${integrationId}
${body}`)}`;
}
function isDerivedSourceEventId(sourceEventId) {
  return sourceEventId.startsWith(DERIVED_SOURCE_EVENT_ID_PREFIX);
}
function normaliseDedupKey(input) {
  const collapsed = input.normalize("NFC").trim().replace(/\s+/g, " ").toLowerCase();
  if (collapsed.length <= DEDUP_KEY_MAX_LENGTH) return collapsed;
  const hash = sha256Hex(collapsed);
  return `${collapsed.slice(0, DEDUP_KEY_MAX_LENGTH - hash.length - 1)}#${hash}`;
}
function truncateTitle(title) {
  if (title.length <= TITLE_MAX_LENGTH) return title;
  return `${title.slice(0, TITLE_MAX_LENGTH - 1)}\u2026`;
}
var CONDITION_OPERATORS = [
  "eq",
  "neq",
  "in",
  "nin",
  "contains",
  "starts_with",
  "ends_with",
  "regex",
  "gt",
  "lt",
  "exists"
];
var REGEX_MAX_LENGTH = 512;
var REGEX_INPUT_MAX_LENGTH = 8192;
var CONDITION_FIELDS = [
  "severity",
  "title",
  "message",
  "category",
  "source_type",
  "event_type",
  "dedup_key",
  "integration_id",
  "source_event_id",
  "sequence",
  "occurred_at",
  "received_at",
  "entity.name",
  "entity.kind",
  "entity.external_id",
  "entity.url",
  "tags",
  "integration.name",
  "integration.type",
  "team",
  "occurrence_count",
  "age_minutes",
  "idle_minutes",
  "state",
  "assignee_id"
];
var CONDITION_FIELD_PREFIXES = ["fields."];
var OperatorSchema = z.enum(CONDITION_OPERATORS);
var ConditionSchema = z.lazy(
  () => z.union([
    z.object({
      field: z.string().min(1).max(256),
      op: OperatorSchema,
      value: z.unknown().optional(),
      ci: z.boolean().optional()
    }).strict(),
    z.object({ all: z.array(ConditionSchema) }).strict(),
    z.object({ any: z.array(ConditionSchema) }).strict(),
    z.object({ not: ConditionSchema }).strict()
  ])
);
function isFieldCondition(c) {
  return typeof c === "object" && c !== null && "field" in c && "op" in c;
}
function isAllGroup(c) {
  return typeof c === "object" && c !== null && "all" in c;
}
function isAnyGroup(c) {
  return typeof c === "object" && c !== null && "any" in c;
}
function isNotGroup(c) {
  return typeof c === "object" && c !== null && "not" in c;
}
var RegexValidationError = class extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
    this.name = "RegexValidationError";
  }
  code;
};
var BACKREFERENCE = /\\[1-9]|\\k<|\(\?P=/;
function checkRegexPattern(pattern) {
  if (pattern.length > REGEX_MAX_LENGTH) {
    throw new RegexValidationError(
      "regex_too_long",
      `pattern is ${pattern.length} characters; the maximum is ${REGEX_MAX_LENGTH}`
    );
  }
  if (BACKREFERENCE.test(pattern)) {
    throw new RegexValidationError("regex_backreference", "back-references are not supported");
  }
  try {
    RE2JS.compile(pattern);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new RegexValidationError("regex_invalid", detail);
  }
}
var MAX_CACHE_ENTRIES = 2e3;
var cache = /* @__PURE__ */ new Map();
function compileRegex(pattern, caseInsensitive = false) {
  const key = `${caseInsensitive ? "i" : "s"}:${pattern}`;
  const hit = cache.get(key);
  if (hit) return hit;
  checkRegexPattern(pattern);
  const compiled = RE2JS.compile(pattern, caseInsensitive ? RE2JS.CASE_INSENSITIVE : 0);
  if (cache.size >= MAX_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== void 0) cache.delete(oldest);
  }
  cache.set(key, compiled);
  return compiled;
}
function regexCacheSize() {
  return cache.size;
}
function clearRegexCache() {
  cache.clear();
}
function truncateRegexInput(input) {
  return input.length > REGEX_INPUT_MAX_LENGTH ? input.slice(0, REGEX_INPUT_MAX_LENGTH) : input;
}
function regexTest(compiled, input) {
  return compiled.matcher(truncateRegexInput(input)).find();
}

// src/dsl/validate.ts
var CONDITION_MAX_DEPTH = 16;
var FIELD_PATH = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z0-9_@$-]+)*$/;
function isValidFieldPath(field) {
  if (field.length > 256 || !FIELD_PATH.test(field)) return false;
  if (CONDITION_FIELDS.includes(field)) return true;
  return CONDITION_FIELD_PREFIXES.some((p) => field.startsWith(p) && field.length > p.length);
}
function isOperator(op) {
  return typeof op === "string" && CONDITION_OPERATORS.includes(op);
}
function isPlainObject(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function isScalar(v) {
  return typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}
function validateLeaf(node, path, errors) {
  const extra = Object.keys(node).filter((k) => !["field", "op", "value", "ci"].includes(k));
  if (extra.length > 0) {
    errors.push({ path, code: "invalid_shape", message: `unexpected keys: ${extra.join(", ")}` });
  }
  const { field, op, value, ci } = node;
  if (typeof field !== "string" || !isValidFieldPath(field)) {
    errors.push({
      path: `${path}/field`,
      code: "invalid_field",
      message: `unknown or malformed field path ${JSON.stringify(field)}`
    });
  }
  if (!isOperator(op)) {
    errors.push({
      path: `${path}/op`,
      code: "unknown_op",
      message: `unknown operator ${JSON.stringify(op)}`
    });
    return;
  }
  if (ci !== void 0 && typeof ci !== "boolean") {
    errors.push({ path: `${path}/ci`, code: "invalid_value", message: "ci must be a boolean" });
  }
  switch (op) {
    case "in":
    case "nin":
      if (!Array.isArray(value) || !value.every(isScalar)) {
        errors.push({
          path: `${path}/value`,
          code: "invalid_value",
          message: `${op} requires an array of scalar values`
        });
      }
      break;
    case "contains":
    case "starts_with":
    case "ends_with":
      if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") {
        errors.push({
          path: `${path}/value`,
          code: "invalid_value",
          message: `${op} requires a scalar value`
        });
      }
      break;
    case "regex":
      if (typeof value !== "string") {
        errors.push({
          path: `${path}/value`,
          code: "invalid_value",
          message: "regex requires a string pattern"
        });
        break;
      }
      try {
        checkRegexPattern(value);
      } catch (err) {
        if (err instanceof RegexValidationError) {
          errors.push({ path: `${path}/value`, code: err.code, message: err.message });
        } else {
          throw err;
        }
      }
      break;
    case "gt":
    case "lt":
      if (typeof value !== "number" && typeof value !== "string") {
        errors.push({
          path: `${path}/value`,
          code: "invalid_value",
          message: `${op} requires a number or string`
        });
      }
      break;
    case "exists":
      if (value !== void 0 && typeof value !== "boolean") {
        errors.push({
          path: `${path}/value`,
          code: "invalid_value",
          message: "exists takes an optional boolean"
        });
      }
      break;
    case "eq":
    case "neq":
      if (value === void 0 || value === null || !isScalar(value)) {
        errors.push({
          path: `${path}/value`,
          code: "invalid_value",
          message: `${op} requires a scalar value (use exists to test whether a field is set)`
        });
      }
      break;
  }
}
function walk(node, path, depth, errors) {
  if (depth > CONDITION_MAX_DEPTH) {
    errors.push({ path, code: "too_deep", message: `nesting deeper than ${CONDITION_MAX_DEPTH}` });
    return;
  }
  if (!isPlainObject(node)) {
    errors.push({ path, code: "invalid_shape", message: "condition must be an object" });
    return;
  }
  const keys = Object.keys(node);
  if ("all" in node || "any" in node) {
    const kind = "all" in node ? "all" : "any";
    if (keys.length !== 1) {
      errors.push({
        path,
        code: "invalid_shape",
        message: `${kind} group must contain only "${kind}"`
      });
    }
    const children = node[kind];
    if (!Array.isArray(children)) {
      errors.push({
        path: `${path}/${kind}`,
        code: "invalid_shape",
        message: `${kind} must be an array`
      });
      return;
    }
    children.forEach((child, i) => walk(child, `${path}/${kind}/${i}`, depth + 1, errors));
    return;
  }
  if ("not" in node) {
    if (keys.length !== 1) {
      errors.push({ path, code: "invalid_shape", message: 'not group must contain only "not"' });
    }
    walk(node.not, `${path}/not`, depth + 1, errors);
    return;
  }
  if ("field" in node || "op" in node) {
    validateLeaf(node, path, errors);
    return;
  }
  errors.push({
    path,
    code: "invalid_shape",
    message: "expected {field, op, value} or one of {all}, {any}, {not}"
  });
}
function validateCondition(json) {
  const errors = [];
  walk(json, "", 0, errors);
  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, condition: json, errors: [] };
}
var ConditionValidationError = class extends Error {
  constructor(errors) {
    super(`invalid condition: ${errors.map((e) => `${e.path || "/"} ${e.message}`).join("; ")}`);
    this.errors = errors;
    this.name = "ConditionValidationError";
  }
  errors;
};
function parseCondition(json) {
  const result = validateCondition(json);
  if (!result.ok) throw new ConditionValidationError(result.errors);
  return result.condition;
}
function collectLeaves(condition) {
  const out = [];
  const visit = (c) => {
    if ("all" in c) c.all.forEach(visit);
    else if ("any" in c) c.any.forEach(visit);
    else if ("not" in c) visit(c.not);
    else out.push(c);
  };
  visit(condition);
  return out;
}

// src/dsl/compile.ts
function resolvePath(ctx, path) {
  let current = ctx;
  for (const segment of path.split(".")) {
    if (current === null || current === void 0) return void 0;
    if (typeof current !== "object") return void 0;
    if (!Object.hasOwn(current, segment)) return void 0;
    current = current[segment];
  }
  return current;
}
function fold(value, ci) {
  return ci && typeof value === "string" ? value.toLowerCase() : value;
}
function scalarEquals(actual, expected, ci) {
  if (typeof actual === "string" && typeof expected === "string") {
    return fold(actual, ci) === fold(expected, ci);
  }
  if (typeof actual === "number" && typeof expected === "string") {
    const n = Number(expected);
    return !Number.isNaN(n) && actual === n;
  }
  if (typeof actual === "string" && typeof expected === "number") {
    const n = Number(actual);
    return !Number.isNaN(n) && expected === n;
  }
  return actual === expected;
}
function includesScalar(list, needle, ci) {
  return list.some((item) => scalarEquals(item, needle, ci));
}
function asString(value) {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return void 0;
}
function asNumber(value) {
  if (typeof value === "number") return Number.isNaN(value) ? void 0 : value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isNaN(n) ? void 0 : n;
  }
  if (value instanceof Date) return value.getTime();
  return void 0;
}
function compare(actual, expected) {
  const an = asNumber(actual);
  const en = asNumber(expected);
  if (an !== void 0 && en !== void 0) return an - en;
  if (typeof actual === "string" && typeof expected === "string") {
    return actual < expected ? -1 : actual > expected ? 1 : 0;
  }
  return void 0;
}
function stringOp(actual, ci, test) {
  if (Array.isArray(actual)) {
    return actual.some((item) => {
      const s2 = asString(item);
      return s2 !== void 0 && test(fold(s2, ci));
    });
  }
  const s = asString(actual);
  return s !== void 0 && test(fold(s, ci));
}
function compileLeaf(cond) {
  const { field, op, value } = cond;
  const ci = cond.ci === true;
  switch (op) {
    case "exists": {
      const expectExists = value !== false;
      return (ctx) => {
        const actual = resolvePath(ctx, field);
        const exists = actual !== void 0 && actual !== null && actual !== "" && !(Array.isArray(actual) && actual.length === 0);
        return exists === expectExists;
      };
    }
    case "eq":
      return (ctx) => {
        const actual = resolvePath(ctx, field);
        if (Array.isArray(actual)) return includesScalar(actual, value, ci);
        return scalarEquals(actual, value, ci);
      };
    case "neq":
      return (ctx) => {
        const actual = resolvePath(ctx, field);
        if (Array.isArray(actual)) return !includesScalar(actual, value, ci);
        return !scalarEquals(actual, value, ci);
      };
    case "in": {
      const list = Array.isArray(value) ? value : [value];
      return (ctx) => {
        const actual = resolvePath(ctx, field);
        if (Array.isArray(actual)) return actual.some((item) => includesScalar(list, item, ci));
        return includesScalar(list, actual, ci);
      };
    }
    case "nin": {
      const list = Array.isArray(value) ? value : [value];
      return (ctx) => {
        const actual = resolvePath(ctx, field);
        if (Array.isArray(actual)) return !actual.some((item) => includesScalar(list, item, ci));
        return !includesScalar(list, actual, ci);
      };
    }
    case "contains": {
      const needle = fold(asString(value) ?? "", ci);
      return (ctx) => {
        const actual = resolvePath(ctx, field);
        if (Array.isArray(actual)) return includesScalar(actual, value, ci);
        return stringOp(actual, ci, (s) => s.includes(needle));
      };
    }
    case "starts_with": {
      const prefix = fold(asString(value) ?? "", ci);
      return (ctx) => stringOp(resolvePath(ctx, field), ci, (s) => s.startsWith(prefix));
    }
    case "ends_with": {
      const suffix = fold(asString(value) ?? "", ci);
      return (ctx) => stringOp(resolvePath(ctx, field), ci, (s) => s.endsWith(suffix));
    }
    case "regex": {
      const pattern = typeof value === "string" ? value : "";
      const compiled = compileRegex(pattern, ci);
      return (ctx) => {
        const actual = resolvePath(ctx, field);
        if (Array.isArray(actual)) {
          return actual.some((item) => {
            const s2 = asString(item);
            return s2 !== void 0 && regexTest(compiled, s2);
          });
        }
        const s = asString(actual);
        return s !== void 0 && regexTest(compiled, s);
      };
    }
    case "gt":
      return (ctx) => {
        const c = compare(resolvePath(ctx, field), value);
        return c !== void 0 && c > 0;
      };
    case "lt":
      return (ctx) => {
        const c = compare(resolvePath(ctx, field), value);
        return c !== void 0 && c < 0;
      };
  }
}
function compileCondition(condition) {
  const cond = parseCondition(condition);
  return compileNode(cond);
}
function compileNode(cond) {
  if (isFieldCondition(cond)) return compileLeaf(cond);
  if (isAllGroup(cond)) {
    const parts = cond.all.map(compileNode);
    return (ctx) => parts.every((p) => p(ctx));
  }
  if (isAnyGroup(cond)) {
    const parts = cond.any.map(compileNode);
    return (ctx) => parts.some((p) => p(ctx));
  }
  if (isNotGroup(cond)) {
    const inner = compileNode(cond.not);
    return (ctx) => !inner(ctx);
  }
  throw new Error("unreachable: unknown condition node");
}
function evaluateCondition(condition, ctx) {
  return compileCondition(condition)(ctx);
}
function minutesSince(at, now) {
  const then = new Date(at).getTime();
  return Math.max(0, Math.floor(((now ?? /* @__PURE__ */ new Date()).getTime() - then) / 6e4));
}
function contextFromEvent(event, extras = {}) {
  const age = extras.age_minutes ?? (extras.first_seen !== void 0 ? minutesSince(extras.first_seen, extras.now) : void 0);
  const idle = extras.idle_minutes ?? (extras.last_seen !== void 0 ? minutesSince(extras.last_seen, extras.now) : void 0);
  return {
    severity: event.severity,
    title: event.title,
    message: event.message,
    category: event.category,
    source_type: event.source_type,
    event_type: event.event_type,
    dedup_key: event.dedup_key,
    integration_id: event.integration_id,
    source_event_id: event.source_event_id,
    sequence: event.sequence,
    occurred_at: event.occurred_at,
    received_at: event.received_at,
    entity: { ...event.entity },
    fields: { ...event.fields },
    tags: [...event.tags],
    integration: extras.integration ?? { type: event.source_type },
    team: extras.team,
    occurrence_count: extras.occurrence_count,
    age_minutes: age,
    idle_minutes: idle,
    assignee_id: extras.assignee_id ?? void 0,
    state: extras.state
  };
}

export { ACTIVE_ALARM_STATES, ALARM_STATES, AlarmStateSchema, CONDITION_FIELDS, CONDITION_FIELD_PREFIXES, CONDITION_MAX_DEPTH, CONDITION_OPERATORS, CanonicalEventSchema, ConditionSchema, ConditionValidationError, DEDUP_KEY_MAX_LENGTH, DERIVED_SOURCE_EVENT_ID_PREFIX, EMAIL_TEMPLATE_NAMES, ENTITY_KINDS, EVENT_TYPES, EmailTemplateNameSchema, EntityKindSchema, EntitySchema, EventTypeSchema, KNOWN_SOURCE_TYPES, REGEX_INPUT_MAX_LENGTH, REGEX_MAX_LENGTH, RULE_SETTABLE_STATES, RegexValidationError, SEVERITIES, SEVERITY_RANK, SeveritySchema, SourceTypeSchema, TITLE_MAX_LENGTH, checkRegexPattern, clearRegexCache, collectLeaves, compareSeverity, compileCondition, compileRegex, contextFromEvent, deriveSourceEventId, evaluateCondition, isAllGroup, isAnyGroup, isDerivedSourceEventId, isFieldCondition, isKnownSourceType, isMoreSevere, isNotGroup, isValidFieldPath, maxSeverity, minSeverity, normaliseDedupKey, parseCondition, regexCacheSize, regexTest, resolvePath, sha256Hex, stableStringify, truncateRegexInput, truncateTitle, validateCondition };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map